import { supabase } from "@/integrations/supabase/client";
import { ORDER_STATUS, ORDER_SOURCE } from "@/constants/orderConstants";
import { logger } from "@/lib/logger";
import { createActivityEntry } from "@/lib/deliveryLogFormat";
import { appendNoteAtomic } from "@/lib/atomicNoteAppend";
import { mergeContextIntoComments, resolveContextTag } from "@/lib/orderContextPersistence";
import { buildOrderNumber, type OrderSource as OrderNumSource } from "@/lib/orderNumberGenerator";
import { DEFAULT_ORDER_ID_CONFIG, type UserSettings } from "@/hooks/useUserSettings";
import { toZonedTime } from "date-fns-tz";

export interface ParsedOrder {
  orderNumber: string | null;
  customerName: string;
  customerPhone: string | null;
  secondaryPhone?: string | null;
  address: string | null;
  message: string;
  products: Array<{
    name: string;
    quantity?: string;
    unit?: string;
    price?: string;
  }>;
  totalPrice: number | null;
  source: string;
  category: string | null;
  csrName: string | null;
  valid: boolean;
  warnings: string[];
  // Enhanced validation fields
  errors: Array<{ field: string; message: string }>;
  isDuplicate: boolean;
  duplicateReason?: string;
  duplicateOf?: string;
  existingOrderData?: any;
  isEdited: boolean;
  rowState: "valid" | "warning" | "error" | "duplicate" | "rejected";
  confidenceScore?: number;
  fulfillment_type?: string;
  missing_info_reasons?: { phone?: string; address?: string };
}

/**
 * Fire-and-forget: write initial "Order received" activity log for bulk-inserted orders.
 * Uses workspace language from localStorage for consistency.
 */
async function writeInitialActivityLogs(orderIds: string[]) {
  if (orderIds.length === 0) return;
  const lang = (localStorage.getItem("preferred-language") === "ka" ? "ka" : "en") as "en" | "ka";
  const logEntry = createActivityEntry("system", "order_received", undefined, lang);
  // Fire all in parallel, don't await — non-fatal
  Promise.allSettled(
    orderIds.map((id) => appendNoteAtomic(id, logEntry, false))
  ).catch((err) => logger.warn("Failed to write bulk activity logs", { error: String(err) }));
}

export async function bulkInsertOrders(
  orders: ParsedOrder[],
  userId: string,
): Promise<{ success: number; failed: number; duplicates: number; errors: any[] }> {
  // Fetch user's order ID config for auto-generation
  let orderIdConfig = DEFAULT_ORDER_ID_CONFIG;
  try {
    const { data: profile } = await supabase
      .from("profiles")
      .select("settings")
      .eq("id", userId)
      .single();
    const settings = profile?.settings as UserSettings | null;
    if (settings?.orderIdConfig) orderIdConfig = settings.orderIdConfig;
  } catch (e) {
    logger.warn("Failed to fetch order ID config for bulk, using default");
  }

  // Get current sequence base using same logic as useOrderMutations
  const today = toZonedTime(new Date(), 'Asia/Tbilisi');
  let baseSequence = 0;
  try {
    const fmt = orderIdConfig.unifiedFormat;
    if (fmt.dateFormat === 'none') {
      const prefix = fmt.prefix;
      const separator = fmt.separator || '-';
      const searchPattern = prefix ? `${prefix}${separator}%` : `%`;
      const { data: existingOrders } = await supabase
        .from("orders")
        .select("order_number")
        .eq("user_id", userId)
        .like("order_number", searchPattern)
        .order("order_number", { ascending: false })
        .limit(200);
      const escapedPrefix = (prefix || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const escapedSep = separator.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const seqRegex = new RegExp(`^${escapedPrefix}${escapedSep}(\\d+)$`, 'i');
      for (const order of existingOrders || []) {
        if (order.order_number) {
          const match = order.order_number.match(seqRegex);
          if (match) {
            const num = parseInt(match[1], 10);
            if (num > baseSequence) baseSequence = num;
          }
        }
      }
    } else {
      const startOfDay = new Date(today);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(today);
      endOfDay.setHours(23, 59, 59, 999);
      const { count } = await supabase
        .from("orders")
        .select("id", { count: 'exact', head: true })
        .eq("user_id", userId)
        .gte("created_at", startOfDay.toISOString())
        .lte("created_at", endOfDay.toISOString());
      baseSequence = count || 0;
    }
  } catch (e) {
    logger.warn("Failed to determine sequence base for bulk order numbers");
  }

  // Filter out invalid, duplicate, and rejected orders
  let sequenceCounter = baseSequence;
  const ordersToInsert = orders
    .filter((order) => order.rowState !== "rejected" && !order.isDuplicate && order.valid)
    .map((order) => {
      // Add secondary phone to comments if present
      let comments = "";
      if (order.secondaryPhone) {
        comments = `Additional contact: ${order.secondaryPhone}`;
      }

      // Persist fulfillment context into comments + context_tag
      const hasPartialPayment = order.warnings?.includes("partial_payment");
      const persistedComments = mergeContextIntoComments(
        comments || null,
        order.fulfillment_type as any,
        order.missing_info_reasons as any,
        hasPartialPayment,
      );
      const persistedContextTag = resolveContextTag(
        null,
        order.fulfillment_type as any,
      );

      // Auto-generate order number if not present
      sequenceCounter++;
      const orderNumber = order.orderNumber || buildOrderNumber(orderIdConfig, 'ai' as OrderNumSource, sequenceCounter, today);

      return {
        order_number: orderNumber,
        customer_name: order.customerName,
        customer_phone: order.customerPhone,
        address: order.address,
        message_raw: order.message,
        parsed_items: JSON.stringify(order.products),
        total_price: order.totalPrice,
        source: order.source || ORDER_SOURCE.VIBER,
        category: order.category,
        order_status: ORDER_STATUS.NEW,
        csr_name: order.csrName,
        comments: persistedComments,
        user_id: userId,
        context_tag: persistedContextTag,
      };
    });

  if (ordersToInsert.length === 0) {
    return { success: 0, failed: 0, duplicates: 0, errors: [] };
  }

  const errors: any[] = [];
  let successCount = 0;

  // Optimized approach: Try batch insert first for performance
  // If it fails, fall back to individual inserts for detailed error tracking
  const { data: batchData, error: batchError } = await supabase.from("orders").insert(ordersToInsert).select();

  if (!batchError && batchData) {
    // Batch insert succeeded - fastest path
    successCount = batchData.length;
    // Write initial activity logs for all inserted orders (fire-and-forget)
    writeInitialActivityLogs(batchData.map((o: any) => o.id));
  } else {
    // Batch failed - insert individually to identify which orders failed
    logger.warn("Batch insert failed, retrying individually", { error: batchError?.message });

    const insertedIds: string[] = [];
    for (const order of ordersToInsert) {
      const { data, error } = await supabase.from("orders").insert([order]).select();

      if (error) {
        logger.error("Insert error for order", error, { orderNumber: order.order_number });
        errors.push({ order: order.order_number, error: error.message });
      } else if (data) {
        successCount += data.length;
        insertedIds.push(...data.map((o: any) => o.id));
      }
    }
    // Write initial activity logs (fire-and-forget)
    writeInitialActivityLogs(insertedIds);
  }

  const duplicateCount = orders.filter((o) => o.isDuplicate).length;

  return {
    success: successCount,
    failed: errors.length,
    duplicates: duplicateCount,
    errors,
  };
}

// ============= BULK ORDER PROCESSING =============
// Edge function handles semantic chunking, parallel processing, and AI calls internally
// Frontend just sends the raw data and receives structured results

interface BatchResult {
  success: boolean;
  orders: ParsedOrder[];
  summary?: {
    total: number;
    valid: number;
    validationErrors: number;
    warnings: any[];
    processingTimeMs: number;
    chunksProcessed?: number;
  };
  error?: string;
}

/**
 * Process bulk orders via edge function
 *
 * The edge function handles:
 * - Semantic chunking (splits text intelligently without breaking conversations)
 * - Parallel processing (3 chunks at once)
 * - Conversation type detection (new_order vs inquiry vs complaint)
 * - Context tracking across messages
 * - Phone number normalization
 *
 * @param method - 'excel' for structured exports, 'text' for raw chat
 * @param data - The text content (already parsed from file if needed)
 */
async function processBulkOrders(method: "excel" | "text", data: string): Promise<BatchResult> {
  try {
    logger.info("Calling bulk-process-orders edge function", {
      method,
      dataLength: data.length,
      estimatedOrders: Math.floor(data.length / 200), // Rough estimate
    });

    const { data: result, error } = await supabase.functions.invoke("bulk-process-orders", {
      body: { method, data },
    });

    if (error) {
      logger.error("Edge function error", error);
      
      // Extract specific error codes from the error context
      const errorContext = (error as any).context;
      let errorCode = "UNKNOWN_ERROR";
      let errorMessage = error.message;
      
      // Check for HTTP status codes in the error
      if (errorMessage?.includes("429") || errorContext?.status === 429) {
        errorCode = "RATE_LIMIT";
        errorMessage = "Too many requests. Please wait and try again.";
      } else if (errorMessage?.includes("402") || errorContext?.status === 402) {
        errorCode = "CREDITS_REQUIRED";
        errorMessage = "AI processing credits required.";
      } else if (errorMessage?.includes("500") || errorContext?.status === 500) {
        errorCode = "PROCESSING_FAILED";
      }
      
      return {
        success: false,
        orders: [],
        error: errorCode,
      };
    }

    if (!result.success) {
      logger.warn("Edge function returned unsuccessful result", {
        error: result.error,
        message: result.message,
        requestId: result.requestId,
      });
      return {
        success: false,
        orders: result.orders || [],
        error: result.error || result.message,
      };
    }

    logger.info("Bulk processing complete", {
      totalOrders: result.summary?.total || result.orders?.length || 0,
      validOrders: result.summary?.valid || 0,
      processingTimeMs: result.summary?.processingTimeMs || 0,
      chunksProcessed: result.summary?.chunksProcessed || 1,
      requestId: result.requestId,
    });

    return {
      success: true,
      orders: result.orders || [],
      summary: result.summary,
      error: undefined,
    };
  } catch (error) {
    logger.error("Bulk processing fetch error", error);
    return {
      success: false,
      orders: [],
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
}

export interface BulkProcessResult {
  success: boolean;
  orders: ParsedOrder[];
  summary: {
    total: number;
    valid: number;
    warnings: any[];
    processingTimeMs: number;
    chunksProcessed?: number;
  };
  error?: string;
}

/**
 * Main entry point for bulk order processing
 *
 * Usage:
 * - Excel/CSV: Parse file to text first, then call with method='excel'
 * - Raw chat: Use text directly with method='text'
 *
 * The edge function handles all intelligent processing:
 * - Semantic chunking (doesn't break mid-conversation)
 * - Parallel processing (2-3x faster)
 * - Context understanding (corrections, negotiations, multi-message orders)
 * - Conversation type detection (orders vs inquiries vs complaints)
 *
 * Expected performance:
 * - Excel (25 orders): ~8-10 seconds
 * - Text (25 orders): ~12-15 seconds
 * - Scales well: 100 orders in ~30-40 seconds
 */
export async function processBulkOrdersWithProgress(
  method: "excel" | "text",
  data: string,
  onProgress?: (completed: number, total: number) => void,
): Promise<BulkProcessResult> {
  const startTime = Date.now();

  try {
    // Single call to edge function - it handles chunking internally
    onProgress?.(0, 1);

    const result = await processBulkOrders(method, data);

    onProgress?.(1, 1);

    const processingTimeMs = Date.now() - startTime;

    // Edge function might not return summary in error cases
    const summary = result.summary || {
      total: result.orders?.length || 0,
      valid: result.orders?.filter((o) => o.valid).length || 0,
      warnings: [],
      processingTimeMs,
      chunksProcessed: 1,
    };

    return {
      success: result.success,
      orders: result.orders || [],
      summary: {
        ...summary,
        processingTimeMs, // Use total time including network
      },
      error: result.error,
    };
  } catch (error) {
    logger.error("Bulk processing error", error);
    return {
      success: false,
      orders: [],
      summary: {
        total: 0,
        valid: 0,
        warnings: [],
        processingTimeMs: Date.now() - startTime,
        chunksProcessed: 0,
      },
      error: error instanceof Error ? error.message : "Unknown error",
    };
  }
}

// Export as default for backward compatibility
export { processBulkOrdersWithProgress as processBulkOrders };
